import { z } from 'zod';

import { ZodAmount, ZodIntegerAmountValue } from '@Common/types/Amount';
import { ZodB2bSavingPlanCategory, ZodB2bSavingPlanType } from '@Common/types/b2b/plans';
import {
  ZodB2bCampaignActionHandler,
  ZodB2bDaysIncrementStep,
} from '@Common/types/bo/b2b/contracts';
import {
  ZodEntryFee,
  ZodMatchingContributionOrderTaxes,
} from '@Common/types/explore/mutual_fund_order';
import { ZodProfileSavingPlanId } from '@Common/types/profile/saving_plans';
import { ZodId } from '@Common/types/request';
import { ZodSuccessResponse } from '@Common/types/response';
import { ZodDateTimeString } from '@Common/types/zod/zod-date-string';
import { endpoint, GetEndpointType } from '@Common/types/zod/zod-endpoint';
import { createZodParamsValidator } from '@Common/types/zod/zod-params';
import { ZodTrimmedString } from '@Common/types/zod/zod-trimmed-string';

export const ZodAmountInDays = z
  .number()
  .min(0)
  .max(999)
  .refine((v) => Number.isInteger(v * 4), {
    message: 'amount_in_days must be in 0.25 increments',
  });

export const B2bCampaignAllocationDayTypeValues = [
  'cet',
  'rtt',
  'overtime',
  'paid_leave',
  'seniority_leave',
  'untaken_rest',
] as const;
export const ZodB2bCampaignAllocationDayType = z.enum(B2bCampaignAllocationDayTypeValues);
export type B2bCampaignAllocationDayType = z.infer<typeof ZodB2bCampaignAllocationDayType>;

const ZodAllocationAmount = z.object({
  amount: ZodIntegerAmountValue.min(1),
});

const ZodAllocationDays = z.object({
  amount_in_days: ZodAmountInDays,
  days_type: ZodB2bCampaignAllocationDayType.default('cet'),
});

const ZodCampaignPayoutAllocation = z.object({
  ...ZodAllocationAmount.shape,
  type: z.literal('payout'),
  mdlr_beneficiary_id: z.number().int().optional(),
});

export type CampaignPayoutAllocation = z.infer<typeof ZodCampaignPayoutAllocation>;

const ZodInvestmentBase = z.object({
  type: z.literal('investment'),
  saving_plan_id: ZodProfileSavingPlanId,
  instrument_id: z.number().int().nullable(),
});

const ZodCampaignInvestmentAllocationAmount = ZodInvestmentBase.merge(ZodAllocationAmount);

export type CampaignInvestmentAllocationAmount = z.infer<
  typeof ZodCampaignInvestmentAllocationAmount
>;

const ZodCampaignInvestmentAllocationDays = ZodInvestmentBase.merge(ZodAllocationDays);

export type CampaignInvestmentAllocationDays = z.infer<typeof ZodCampaignInvestmentAllocationDays>;

export type CampaignInvestmentAllocation =
  | CampaignInvestmentAllocationAmount
  | CampaignInvestmentAllocationDays;

const ZodCampaignAllocationAmount = z.discriminatedUnion('type', [
  ZodCampaignPayoutAllocation,
  ZodCampaignInvestmentAllocationAmount,
]);

export type CampaignAllocationAmount = z.infer<typeof ZodCampaignAllocationAmount>;

const ZodCampaignAllocationDays = ZodCampaignInvestmentAllocationDays;

export type CampaignAllocationDays = z.infer<typeof ZodCampaignAllocationDays>;

export type CampaignAllocation = CampaignAllocationAmount | CampaignAllocationDays;

const CampaignTocsTermValues = ['key_information_documents', 'legal_bonus_information'] as const;
export const ZodCampaignTocsTerm = z.enum(CampaignTocsTermValues);
export type CampaignTocsTerm = z.infer<typeof ZodCampaignTocsTerm>;

export const ZodEmployeeSavingsCampaignInvestBody = z.discriminatedUnion('allocation_unit', [
  z.object({
    allocation_unit: z.literal('amount'),
    campaign_id: z.number().int(),
    allocations: z
      .array(ZodCampaignAllocationAmount)
      .min(1)
      .refine(
        (a) => a.filter((x) => x.type === 'payout').length <= 1,
        "Max one allocation with type 'payout' is allowed",
      ),
    agreed_terms: ZodCampaignTocsTerm.array(),
  }),
  z.object({
    allocation_unit: z.literal('days'),
    campaign_id: z.number().int(),
    allocations: z.array(ZodCampaignAllocationDays).min(1),
    agreed_terms: ZodCampaignTocsTerm.array(),
  }),
]);

export type EmployeeSavingsCampaignInvestBody = z.infer<
  typeof ZodEmployeeSavingsCampaignInvestBody
>;

export const ZodEmployeeSavingsCampaignInvestResponseBody = ZodSuccessResponse;

export const postExploreEmployeeSavingsCampaignInvestEndpoint = endpoint({
  method: 'POST',
  body: ZodEmployeeSavingsCampaignInvestBody,
  responseBody: ZodEmployeeSavingsCampaignInvestResponseBody,
});

export type PostExploreEmployeeSavingsCampaignInvestEndpoint = GetEndpointType<
  typeof postExploreEmployeeSavingsCampaignInvestEndpoint
>;

const CampaignTypeValues = [
  'participation',
  'profit_sharing',
  'value_sharing_bonus',
  'voluntary_deposit',
  'unused_vacation_days',
  'ccb_matured_election',
  'withdrawal_at_maturity',
  'matching_contribution',
] as const;
export const ZodCampaignType = z.enum(CampaignTypeValues);
export type EmployeeSavingsCampaignType = z.infer<typeof ZodCampaignType>;

const ZodSavingsCampaignDayTypeBalance = z.object({
  total_days: ZodAmountInDays.describe('Days granted for this type (quota file)'),
  available_days: ZodAmountInDays.describe(
    'Days not yet allocated: total_days minus non-cancelled choices of this type',
  ),
});

export const ZodSavingsCampaign = z.object({
  end_date: ZodDateTimeString,
  start_date: ZodDateTimeString,
  bonus_available: z.number(),
  campaign_type: ZodCampaignType,
  campaign_id: z.number().int(),
  b2b_company_id: z.number().int(),
  payout_processed_by: ZodB2bCampaignActionHandler.nullable(),
  supported_plan_ids: z.array(ZodProfileSavingPlanId),
  total_campaign_gross_amount: ZodAmount.nullable(),
  total_group_campaign_gross_amount: ZodAmount.nullable(),
  is_payout_only: z.boolean(),
  available_bonus_amount: ZodAmount.nullable(),
  available_bonus_vacation_days: ZodAmountInDays.nullable(),
  available_bonus_vacation_days_by_type: z
    .record(ZodB2bCampaignAllocationDayType, ZodSavingsCampaignDayTypeBalance)
    .nullable()
    .describe(
      'Balance keyed by day type. Every day type is present, with total_days = 0 when none are available. Null when the campaign is not day-based.',
    ),
  source_instrument_id: z.number().int().nullable(),
  days_increment_step: ZodB2bDaysIncrementStep.nullable().describe(
    'Step for +/- and split evenly. Null means not configured: clients use the 0.25 default.',
  ),
  has_cet_agreement: z
    .boolean()
    .nullable()
    .describe(
      'true: Time Savings Account (unused days carry forward). false: Monetised Days Off (unused days are lost). Null when the campaign is not day-based.',
    ),
});

export type EmployeeSavingsCampaign = z.infer<typeof ZodSavingsCampaign>;

export const ZodExploreEmployeeSavingsCampaignResponse = z.object({
  campaigns: z
    .array(ZodSavingsCampaign)
    .refine(
      (campaigns) =>
        campaigns.every(
          (campaign) =>
            campaign.campaign_type === 'withdrawal_at_maturity' ||
            campaign.supported_plan_ids.length >= 1,
        ),
      {
        message: 'expected supported_plan_ids array to have >=1 items',
      },
    ),
});

export const getExploreEmployeeSavingsCampaignEndpoint = endpoint({
  method: 'GET',
  responseBody: ZodExploreEmployeeSavingsCampaignResponse,
});

export type GetExploreEmployeeSavingsCampaignEndpoint = GetEndpointType<
  typeof getExploreEmployeeSavingsCampaignEndpoint
>;

const ZodEmployeeSavingsCustomCampaignPayoutAllocation = z.object({
  ...ZodAllocationAmount.shape,
  type: z.literal('payout'),
});

export type EmployeeSavingsCustomCampaignPayoutAllocation = z.infer<
  typeof ZodEmployeeSavingsCustomCampaignPayoutAllocation
>;

const ZodEmployeeSavingsCustomCampaignAllocationAmount = z.discriminatedUnion('type', [
  ZodEmployeeSavingsCustomCampaignPayoutAllocation,
  ZodCampaignInvestmentAllocationAmount,
]);

export type EmployeeSavingsCustomCampaignAllocationAmount = z.infer<
  typeof ZodEmployeeSavingsCustomCampaignAllocationAmount
>;

const ZodEmployeeSavingsCustomCampaignAllocationDays = ZodCampaignInvestmentAllocationDays;

export type EmployeeSavingsCustomCampaignAllocationDays = z.infer<
  typeof ZodEmployeeSavingsCustomCampaignAllocationDays
>;

export type EmployeeSavingsCustomCampaignAllocation =
  | EmployeeSavingsCustomCampaignAllocationAmount
  | EmployeeSavingsCustomCampaignAllocationDays;

export const ZodEmployeeSavingsCustomCampaignAllocationBody = z.discriminatedUnion(
  'allocation_unit',
  [
    z.object({
      allocation_unit: z.literal('amount'),
      campaign_id: z.number().int(),
      allocations: z
        .array(ZodEmployeeSavingsCustomCampaignAllocationAmount)
        .min(1)
        .refine(
          (a) => a.filter((x) => x.type === 'payout').length <= 1,
          "Max one allocation with type 'payout' is allowed",
        ),
    }),
    z.object({
      allocation_unit: z.literal('days'),
      campaign_id: z.number().int(),
      allocations: z.array(ZodEmployeeSavingsCustomCampaignAllocationDays).min(1),
    }),
  ],
);

export type EmployeeSavingsCustomCampaignAllocationBody = z.infer<
  typeof ZodEmployeeSavingsCustomCampaignAllocationBody
>;

const ZodCampaignInvestmentAllocationItemAmount = z.object({
  instrument_id: z.number().int(),
  amount_gross: ZodAmount,
  entry_fee: ZodEntryFee.nullable(),
  saving_plan_id: ZodProfileSavingPlanId,
  saving_plan_type: ZodB2bSavingPlanType,
});

const ZodCampaignInvestmentAllocationItemDays = z.object({
  instrument_id: z.number().int(),
  days: ZodAmountInDays,
  saving_plan_id: ZodProfileSavingPlanId,
  saving_plan_type: ZodB2bSavingPlanType,
});

const ZodCampaignInvestmentAmount = z.object({
  allocations: z.array(ZodCampaignInvestmentAllocationItemAmount),
  total_amount_net: ZodAmount,
  total_amount_gross: ZodAmount,
  // Entry fee details are per individual instrument.
  // Here we list the total entry fee paid by employee
  // FE can lookup in allocations for more details.
  // The same approach is mutual fund order summary endpoint.
  entry_fee: ZodAmount.nullable(),
});

const ZodCampaignInvestmentDays = z.object({
  allocations: z.array(ZodCampaignInvestmentAllocationItemDays),
  total_days: ZodAmountInDays,
  days_by_type: z
    .partialRecord(ZodB2bCampaignAllocationDayType, ZodAmountInDays)
    .nullable()
    .describe('Days allocated keyed by day type.'),
});

const ZodEmployeeSavingsCustomCampaignInvestmentAllocationAmount = z.object({
  ...ZodCampaignInvestmentAmount.shape,
  type: z.literal('investment'),
  plan_type: ZodB2bSavingPlanCategory,
  external_saving_plan_id: ZodProfileSavingPlanId.optional(),
  matching_contribution: z
    .object({
      ...ZodCampaignInvestmentAmount.shape,
      taxes: ZodMatchingContributionOrderTaxes.nullable(),
    })
    .nullable(),
});

export type EmployeeSavingsCustomCampaignInvestmentAllocationAmount = z.infer<
  typeof ZodEmployeeSavingsCustomCampaignInvestmentAllocationAmount
>;

const ZodEmployeeSavingsCustomCampaignInvestmentAllocationDays = z.object({
  ...ZodCampaignInvestmentDays.shape,
  type: z.literal('investment'),
  plan_type: ZodB2bSavingPlanCategory,
});

export type EmployeeSavingsCustomCampaignInvestmentAllocationDays = z.infer<
  typeof ZodEmployeeSavingsCustomCampaignInvestmentAllocationDays
>;

const ZodEmployeeSavingsCustomCampaignInvestmentAllocation = z.union([
  ZodEmployeeSavingsCustomCampaignInvestmentAllocationAmount,
  ZodEmployeeSavingsCustomCampaignInvestmentAllocationDays,
]);

export type EmployeeSavingsCustomCampaignInvestmentAllocation = z.infer<
  typeof ZodEmployeeSavingsCustomCampaignInvestmentAllocation
>;

export const ZodEmployeeSavingsCustomCampaignAllocationSummaryResponse = z.discriminatedUnion(
  'allocation_unit',
  [
    z.object({
      allocation_unit: z.literal('amount'),
      total_amount_gross: ZodAmount,
      allocations: z.array(
        z.discriminatedUnion('type', [
          z.object({ type: z.literal('payout'), amount: ZodAmount }),
          ZodEmployeeSavingsCustomCampaignInvestmentAllocationAmount,
        ]),
      ),
      matching_contribution: z
        .object({
          amount_gross: ZodAmount,
        })
        .nullable(),
      company_name: ZodTrimmedString,
    }),
    z.object({
      allocation_unit: z.literal('days'),
      allocations: z.array(ZodEmployeeSavingsCustomCampaignInvestmentAllocationDays),
      company_name: ZodTrimmedString,
    }),
  ],
);

export const postExploreEmployeeSavingsCustomCampaignAllocationSummaryEndpoint = endpoint({
  method: 'POST',
  body: ZodEmployeeSavingsCustomCampaignAllocationBody,
  responseBody: ZodEmployeeSavingsCustomCampaignAllocationSummaryResponse,
});

export type PostExploreEmployeeSavingsCustomCampaignAllocationSummaryEndpoint = GetEndpointType<
  typeof postExploreEmployeeSavingsCustomCampaignAllocationSummaryEndpoint
>;

export const ZodGetExploreEmployeeSavingsCampaignIdDefaultAllocationsParams =
  createZodParamsValidator(
    z.object({
      id: ZodId,
    }),
  );

export const ZodExploreEmployeeSavingsCampaignDefaultAllocationsResponse = z.object({
  allocations: z.array(
    z.discriminatedUnion('type', [
      z.object({
        type: z.literal('payout'),
        amount: ZodAmount,
      }),
      z.object({
        type: z.literal('investment'),
        // Pilote plan must be aggregated (one allocation)
        instrument_id: z.number().int().nullable(),
        amount: ZodAmount,
        saving_plan_id: ZodProfileSavingPlanId,
        saving_plan_type: ZodB2bSavingPlanType,
      }),
    ]),
  ),
  source_instrument_id: z.number().int().nullable(),
  target_instrument_id: z.number().int().nullable(),
});

export type ExploreEmployeeSavingsCampaignDefaultAllocationsResponse = z.infer<
  typeof ZodExploreEmployeeSavingsCampaignDefaultAllocationsResponse
>;

export const getExploreEmployeeSavingsCampaignIdDefaultAllocationsEndpoint = endpoint({
  method: 'GET',
  pathParams: ZodGetExploreEmployeeSavingsCampaignIdDefaultAllocationsParams,
  queryParams: createZodParamsValidator(
    z.object({
      amount: ZodIntegerAmountValue.min(1),
    }),
  ),
  responseBody: ZodExploreEmployeeSavingsCampaignDefaultAllocationsResponse,
});

export type GetExploreEmployeeSavingsCampaignIdDefaultAllocationsEndpoint = GetEndpointType<
  typeof getExploreEmployeeSavingsCampaignIdDefaultAllocationsEndpoint
>;
